<template>
    <div>
        <div class="h6">Materials Calculator</div>
        <div class="my-2">
            <b-alert
                variant="warning"
                class="my-2"
                show
                v-if="!materials || materials.length === 0"
            >
                No Materials. Use the 'Add Material' button to add some.
            </b-alert>

            <b-table-simple v-else>
                <b-thead>
                    <b-th style="width: 5%"></b-th>
                    <b-th style="width: 35%">Name</b-th>
                    <b-th style="width: 35%">Printable</b-th>
                    <b-th style="width: 10%">Type</b-th>
                    <b-th style="width: 5%">Quantity</b-th>
                    <b-th style="width: 5%">
                        Teacher Notes
                        <b-btn
                            v-b-tooltip.hover
                            title="Should the user refer to the Teacher Notes section for this material."
                            variant="link"
                            class="p-0"
                            tabindex="-1"
                        >
                            <font-awesome-icon :icon="['fad', 'info-circle']"/>
                        </b-btn>
                    </b-th>
                    <b-th style="width: 5%"></b-th>
                </b-thead>

                <draggable
                    v-model="materials"
                    group="items"
                    tag="tbody"
                    handle=".drag-handle"
                    @end="handleDragEnd"
                    :disabled="disabled"
                >
                    <b-tr v-for="material in materials" :key="material.id">
                        <b-td>
                            <div class="d-flex justify-content-center align-items-center drag-handle">
                                <font-awesome-icon
                                    :icon="['fal', 'bars']"
                                    size="2x"
                                    fixed-width
                                    style="color: gainsboro"
                                />
                            </div>
                        </b-td>
                        <b-td>{{ material.name }}</b-td>
                        <b-td>
                            <template v-if="material.worksheet">
                                <b-link
                                    v-if="material.worksheet.cdn_url"
                                    :href="material.worksheet.cdn_url"
                                    target="_blank"
                                >
                                    {{ material.worksheet.title }}
                                    <font-awesome-icon :icon="['fad', 'external-link']" fixed-width/>
                                </b-link>
                                <span v-else>
                                    {{ material.worksheet.title }}
                                </span>
                            </template>
                            <span v-else>
                                N/A
                            </span>
                        </b-td>
                        <b-td>
                            {{ perTypeMapper(material) }}
                        </b-td>
                        <b-td>{{ material.quantity }}</b-td>
                        <b-td>
                            <div class="d-flex justify-content-center align-items-center">
                                <font-awesome-icon
                                    :icon="['fal', material.check_teacher_notes ? 'check' : 'times']"
                                    size="2x"
                                    fixed-width
                                />
                            </div>
                        </b-td>
                        <b-td>
                            <b-dropdown
                                size="sm"
                                variant="secondary"
                                :disabled="disabled"
                                dropleft
                            >
                                <template #button-content>
                                    <font-awesome-icon :icon="['fad', 'cog']" />
                                </template>
                                <b-dropdown-item
                                    @click.prevent="edit(material.id)"
                                    :disabled="!can_update_article || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width />
                                    Edit
                                </b-dropdown-item>
                                <b-dropdown-item
                                    @click.prevent="remove(material.id)"
                                    :disabled="!can_update_article || disabled"
                                >
                                    <font-awesome-icon :icon="['fad', 'trash']" class="text-danger" fixed-width />
                                    Remove
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-td>
                    </b-tr>
                </draggable>
            </b-table-simple>
        </div>

        <div class="d-flex">
            <div class="ml-auto">
                <b-btn
                    size="sm"
                    class="ml-3"
                    variant="outline-primary"
                    :disabled="disabled || !can_update_article"
                    @click="show_material_modal = true"
                >
                    <font-awesome-icon :icon="['fad', 'plus-square']"/>
                    Add Material
                </b-btn>
            </div>
        </div>

        <material-modal
            :visible="show_material_modal"
            :selected_material="selected_material"
            @update:visible="show_material_modal = $event; selected_material = null"
            @create:material="linkMaterialToArticle"
            @update:material="fetchMaterials()"
        />
    </div>
</template>

<style>
.drag-handle {
    cursor: grab;
}
</style>

<script>
import draggable from 'vuedraggable'
import {mapState} from 'vuex'
import MaterialModal from '@/components/modals/article/MaterialModal'
import moment from 'moment'

const materialPositionMapper = (material) => ({id: material.id, order_number: material.pivot.order_number})

export default {
    components: {MaterialModal, draggable},
    computed: {
        ...mapState({
            user: state => state.user.user
        }),
        can_update_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article');
        },
    },
    props: [
        'disabled',
        'article_id',
        'unit_id',
        'publication_id',
    ],
    data: () => ({
        show_material_modal: false,
        selected_material: null,
        materials: [],
    }),
    methods: {
        async handleDragEnd() {
            // Update the pivot.order_number field to match the draggable's change in order.
            this.materials.forEach((material, index) => {
                material.pivot.order_number = index
            });
            const mappedMaterials = this.materials.map(materialPositionMapper)

            try {
                await this.axios.patch(
                    `/api/article/materials/position`,
                    { materials: mappedMaterials },
                    {
                        params: {
                            publication_id: this.publication_id,
                            unit_id: this.unit_id,
                            article_id: this.article_id,
                        }
                    }
                )
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to re-order materials: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        edit(id) {
            const material = this.materials.find((material) => material.id === id)
            if (material) {
                this.selected_material = material
                this.show_material_modal = true
            }
        },
        async remove(id) {
            const material = this.materials.find((material) => material.id === id)
            if (!material) {
                return
            }

            const result = await this.$swal({
                html: `Delete material "${material.name}" ?`,
                showCancelButton: true,
                confirmButtonText: `Delete ${material.name}`,
                confirmButtonColor: '#E74C3C',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })

            if (result.isConfirmed) {
                try {
                    await this.axios.delete(`/api/materials/${id}`)
                } catch (e) {
                    await this.$store.dispatch('pushError', {
                        data: {
                            message: `Failed to delete material: ${e.message}`,
                        },
                        time: moment().toISOString(),
                    })
                } finally {
                    await this.fetchMaterials()
                }
            }
        },
        perTypeMapper(material) {
            switch (material.per_type) {
                case 'student': return 'Student'
                case 'group': return `# of Students (${material.default_group_size})`
                case 'classroom': return 'Classroom'
                case 'as_needed': return 'As Needed'
                default: return 'Unknown Type'
            }
        },
        nextPosition() {
            if (this.materials.length === 0) {
                return 0
            }
            const lastMaterial = this.materials[this.materials.length - 1]

            return lastMaterial.pivot.order_number + 1
        },
        async linkMaterialToArticle(material) {
            try {
                const order_number = this.nextPosition()
                const { id } = material
                await this.axios.post(
                    `/api/materials/${id}/article`,
                    {
                        article_id: this.article_id,
                        order_number,
                    },
                    {
                        params: {
                            publication_id: this.publication_id,
                            unit_id: this.unit_id,
                            article_id: this.article_id,
                        }
                    }
                )

                await this.fetchMaterials()
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to link material to article: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
        async fetchMaterials() {
            try {
                const response = await this.axios.get(`/api/materials`, {
                    params: {
                        publication_id: this.publication_id,
                        unit_id: this.unit_id,
                        article_id: this.article_id,
                    }
                })
                this.materials = response.data
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to load materials: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },
    },
    async mounted() {
        await this.fetchMaterials()
    },
}
</script>
